import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Check, X, Clock, PenLine, UserCheck, FileText, Library, ExternalLink,
  Send, Sparkles, Smile,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '@/hooks/useFetch';
import EmojiSubmissionsReviewPanel from './EmojiSubmissionsReviewPanel';

/**
 * AdminModerationTab — combined queue for:
 *  (a) Pending blog posts — approve+edit OR send-back-with-note
 *  (b) Contributor application requests
 *  (c) Community resource submissions — approve+edit-and-publish OR reject
 *
 * Mounted as a tab inside the existing Admin Dashboard.
 */
const AdminModerationTab = () => {
  const [pendingPosts, setPendingPosts] = useState([]);
  const [contributorRequests, setContributorRequests] = useState([]);
  const [resourceSubmissions, setResourceSubmissions] = useState([]);
  const [resourceCategories, setResourceCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);
  const [rejectNote, setRejectNote] = useState({});
  // Modal state: { type: 'post-approve' | 'post-send-back' | 'submission-publish', payload: {...} }
  const [modal, setModal] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [postsRes, reqRes, subRes, catRes] = await Promise.all([
      apiFetch('/api/blog/admin/pending-posts'),
      apiFetch('/api/blog/admin/contributor-requests'),
      apiFetch('/api/resources/admin/submissions'),
      apiFetch('/api/resources/categories'),
    ]);
    if (postsRes.data) setPendingPosts(postsRes.data);
    if (reqRes.data) setContributorRequests(reqRes.data);
    if (subRes.data?.submissions) setResourceSubmissions(subRes.data.submissions);
    if (catRes.data?.categories) setResourceCategories(catRes.data.categories);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // -- Blog post actions ---------------------------------------------

  const openApproveDialog = (post) => {
    setModal({
      type: 'post-approve',
      payload: {
        post_id: post.post_id,
        title: post.title || '',
        category: post.category || '',
        tags: Array.isArray(post.tags) ? post.tags.join(', ') : (post.tags || ''),
        excerpt: post.excerpt || '',
      },
    });
  };

  const openSendBackDialog = (post) => {
    setModal({
      type: 'post-send-back',
      payload: { post_id: post.post_id, title: post.title, note: '' },
    });
  };

  const submitApprove = async () => {
    const p = modal.payload;
    setActing(`post-${p.post_id}`);
    const tagsArr = p.tags
      ? p.tags.split(',').map((t) => t.trim()).filter(Boolean)
      : [];
    const { error } = await apiFetch(`/api/blog/admin/pending-posts/${p.post_id}/approve`, {
      method: 'POST',
      body: {
        title: p.title,
        category: p.category,
        tags: tagsArr,
        excerpt: p.excerpt,
      },
    });
    setActing(null);
    setModal(null);
    if (error) toast.error(error);
    else { toast.success('Post approved & published 🎉'); load(); }
  };

  const submitSendBack = async () => {
    const p = modal.payload;
    if (!p.note.trim()) { toast.error('Editor note is required'); return; }
    setActing(`post-${p.post_id}`);
    const { error } = await apiFetch(`/api/blog/admin/pending-posts/${p.post_id}/send-back`, {
      method: 'POST',
      body: { note: p.note.trim() },
    });
    setActing(null);
    setModal(null);
    if (error) toast.error(error);
    else { toast.success('Sent back — author will be emailed the note'); load(); }
  };

  // -- Resource submission actions -----------------------------------

  const openPublishSubmissionDialog = (sub) => {
    setModal({
      type: 'submission-publish',
      payload: {
        submission_id: sub.submission_id,
        name: sub.name || '',
        url: sub.url || '',
        description: sub.about || '',
        categories: Array.isArray(sub.categories) ? [...sub.categories] : [],
      },
    });
  };

  const toggleModalCategory = (cat) => {
    setModal((m) => {
      if (!m) return m;
      const cur = m.payload.categories || [];
      const next = cur.includes(cat) ? cur.filter((c) => c !== cat) : [...cur, cat];
      return { ...m, payload: { ...m.payload, categories: next } };
    });
  };

  const submitPublishSubmission = async () => {
    const p = modal.payload;
    if (!p.name.trim() || !p.url.trim()) {
      toast.error('Name and URL are required'); return;
    }
    setActing(`sub-${p.submission_id}`);
    const { error } = await apiFetch(`/api/resources/admin/submissions/${p.submission_id}/publish`, {
      method: 'POST',
      body: {
        name: p.name.trim(),
        url: p.url.trim(),
        description: p.description.trim(),
        categories: p.categories,
      },
    });
    setActing(null);
    setModal(null);
    if (error) toast.error(error);
    else { toast.success('Published to /resources'); load(); }
  };

  const rejectSubmission = async (id) => {
    setActing(`sub-${id}`);
    const { error } = await apiFetch(`/api/resources/admin/submissions/${id}?status=rejected`, {
      method: 'PATCH',
    });
    setActing(null);
    if (error) toast.error(error);
    else { toast.success('Submission rejected'); load(); }
  };

  // -- Contributor request actions ----------------------------------

  const approveRequest = async (id) => {
    setActing(`req-${id}`);
    const { error } = await apiFetch(`/api/blog/admin/contributor-requests/${id}/approve`, { method: 'POST' });
    if (error) toast.error(error);
    else { toast.success('Promoted to contributor'); load(); }
    setActing(null);
  };

  const rejectRequest = async (id) => {
    setActing(`req-${id}`);
    const { error } = await apiFetch(`/api/blog/admin/contributor-requests/${id}/reject`, {
      method: 'POST',
      body: { note: rejectNote[id] || '' },
    });
    if (error) toast.error(error);
    else { toast.success('Request rejected'); load(); }
    setActing(null);
  };

  const pendingReq = contributorRequests.filter((r) => r.status === 'pending');
  const pendingSubs = resourceSubmissions.filter((s) => s.status === 'pending');

  return (
    <Card data-testid="admin-moderation-tab">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="h-5 w-5 text-amber-500" />
          Moderation Queue
        </CardTitle>
        <CardDescription>
          Review pending blog posts and contributor applications.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="posts">
          {/* Mobile: horizontal-scroll + shorter labels so the 3 long
              tab names don't overflow a ~360px viewport. Desktop keeps
              the full labels. */}
          <TabsList
            className="mb-4 w-full justify-start overflow-x-auto whitespace-nowrap [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-thumb]:bg-muted-foreground/30 [&::-webkit-scrollbar-thumb]:rounded-full"
          >
            <TabsTrigger value="posts" data-testid="moderation-posts-tab">
              <FileText className="h-4 w-4 mr-1" />
              <span className="sm:hidden">Posts</span>
              <span className="hidden sm:inline">Pending posts</span>
              {pendingPosts.length > 0 && (
                <Badge variant="destructive" className="ml-2 h-5 px-1.5">{pendingPosts.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="requests" data-testid="moderation-requests-tab">
              <UserCheck className="h-4 w-4 mr-1" />
              <span className="sm:hidden">Requests</span>
              <span className="hidden sm:inline">Contributor requests</span>
              {pendingReq.length > 0 && (
                <Badge variant="destructive" className="ml-2 h-5 px-1.5">{pendingReq.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="submissions" data-testid="moderation-submissions-tab">
              <Library className="h-4 w-4 mr-1" />
              <span className="sm:hidden">Resources</span>
              <span className="hidden sm:inline">Resource submissions</span>
              {pendingSubs.length > 0 && (
                <Badge variant="destructive" className="ml-2 h-5 px-1.5">{pendingSubs.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="emoji-submissions" data-testid="moderation-emoji-submissions-tab">
              <Smile className="h-4 w-4 mr-1" />
              <span className="sm:hidden">Emojis</span>
              <span className="hidden sm:inline">Emoji submissions</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="posts" className="space-y-4">
            {loading ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : pendingPosts.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="empty-posts">No posts awaiting review 🎉</p>
            ) : (
              pendingPosts.map((p) => (
                <Card key={p.post_id} className="border-amber-200/60 dark:border-amber-900/40" data-testid={`pending-post-${p.post_id}`}>
                  <CardContent className="pt-4 space-y-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <PenLine className="h-4 w-4 text-amber-500" />
                          <p className="font-semibold truncate">{p.title}</p>
                          {p.revision_requested && (
                            <Badge variant="outline" className="text-[10px] text-amber-700 border-amber-400">
                              Awaiting revisions
                            </Badge>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
                          <span>By {p.author_name || 'Anonymous'} · {new Date(p.created_at).toLocaleString()}</span>
                          {typeof p.author_last_seen_days === 'number' && (
                            <Badge
                              variant="outline"
                              className={`text-[10px] ${
                                p.author_last_seen_days <= 1
                                  ? 'border-green-500/50 text-green-700 dark:text-green-300'
                                  : p.author_last_seen_days <= 7
                                  ? 'border-amber-400/50 text-amber-700 dark:text-amber-300'
                                  : 'border-red-400/50 text-red-700 dark:text-red-300'
                              }`}
                              title={p.author_last_seen ? `Last seen ${new Date(p.author_last_seen).toLocaleString()}` : ''}
                              data-testid={`author-last-seen-${p.post_id}`}
                            >
                              {p.author_last_seen_days === 0
                                ? 'Author seen today'
                                : p.author_last_seen_days === 1
                                ? 'Author seen yesterday'
                                : `Author seen ${p.author_last_seen_days}d ago`}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <Badge variant="outline" className="shrink-0">{p.submitted_by_role || 'user'}</Badge>
                    </div>
                    <div
                      className="prose prose-sm max-w-none dark:prose-invert max-h-48 overflow-y-auto border rounded p-2 bg-muted/30"
                      dangerouslySetInnerHTML={{ __html: p.content }}
                    />
                    {p.last_send_back_email && (
                      <div
                        className={`text-[11px] rounded px-2 py-1 ${
                          p.last_send_back_email.ok
                            ? 'bg-green-50 text-green-700 dark:bg-green-950/30 dark:text-green-300'
                            : 'bg-red-50 text-red-700 dark:bg-red-950/30 dark:text-red-300'
                        }`}
                        data-testid={`last-email-${p.post_id}`}
                      >
                        {p.last_send_back_email.ok
                          ? `✓ Send-back email delivered to ${p.last_send_back_email.recipient} · ${new Date(p.last_send_back_email.sent_at).toLocaleString()}`
                          : `⚠ Send-back email delivery failed: ${p.last_send_back_email.error || 'unknown error'} · ${p.last_send_back_email.sent_at ? new Date(p.last_send_back_email.sent_at).toLocaleString() : ''}`}
                      </div>
                    )}
                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        onClick={() => openApproveDialog(p)}
                        disabled={acting === `post-${p.post_id}`}
                        className="bg-green-600 hover:bg-green-700 text-white"
                        data-testid={`approve-post-${p.post_id}`}
                      >
                        <Check className="h-4 w-4 mr-1" /> Approve & edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openSendBackDialog(p)}
                        disabled={acting === `post-${p.post_id}`}
                        className="border-amber-300 text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-950/20"
                        data-testid={`send-back-post-${p.post_id}`}
                      >
                        <Send className="h-4 w-4 mr-1" /> Send back for edits
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          <TabsContent value="requests" className="space-y-4">
            {loading ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : contributorRequests.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="empty-requests">No contributor requests yet.</p>
            ) : (
              contributorRequests.map((r) => (
                <Card
                  key={r.request_id}
                  className={r.status === 'pending' ? 'border-amber-200/60 dark:border-amber-900/40' : 'opacity-70'}
                  data-testid={`contributor-request-${r.request_id}`}
                >
                  <CardContent className="pt-4 space-y-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold break-words">{r.user_name}</p>
                        <p className="text-xs text-muted-foreground break-words">
                          <span className="break-all">{r.user_email}</span> · {new Date(r.created_at).toLocaleString()}
                        </p>
                      </div>
                      <Badge variant={r.status === 'pending' ? 'default' : r.status === 'approved' ? 'secondary' : 'outline'} className="shrink-0">
                        {r.status}
                      </Badge>
                    </div>
                    <div className="text-sm space-y-2 break-words">
                      <div>
                        <p className="text-xs font-medium uppercase text-muted-foreground">Why</p>
                        <p className="break-words">{r.why}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium uppercase text-muted-foreground">Experience</p>
                        <p className="break-words">{r.experience}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium uppercase text-muted-foreground">Content topics</p>
                        <p className="break-words">{r.content_topics}</p>
                      </div>
                    </div>
                    {r.status === 'pending' && (
                      <>
                        <Textarea
                          placeholder="Optional internal note (rejection reason, etc.)"
                          value={rejectNote[r.request_id] || ''}
                          onChange={(e) => setRejectNote({ ...rejectNote, [r.request_id]: e.target.value })}
                          rows={2}
                          data-testid={`reject-note-req-${r.request_id}`}
                        />
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => approveRequest(r.request_id)}
                            disabled={acting === `req-${r.request_id}`}
                            className="bg-green-600 hover:bg-green-700 text-white"
                            data-testid={`approve-req-${r.request_id}`}
                          >
                            <Check className="h-4 w-4 mr-1" /> Promote to contributor
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => rejectRequest(r.request_id)}
                            disabled={acting === `req-${r.request_id}`}
                            className="border-red-300 text-red-600 hover:bg-red-50"
                            data-testid={`reject-req-${r.request_id}`}
                          >
                            <X className="h-4 w-4 mr-1" /> Reject
                          </Button>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          {/* --- Resource submissions tab --- */}
          <TabsContent value="submissions" className="space-y-4">
            {loading ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : pendingSubs.length === 0 && resourceSubmissions.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="empty-submissions">
                No resource submissions yet. Share the form via /feedback?tab=submit-resource.
              </p>
            ) : (
              resourceSubmissions.map((s) => {
                const pending = s.status === 'pending';
                return (
                  <Card
                    key={s.submission_id}
                    className={pending ? 'border-amber-200/60 dark:border-amber-900/40' : 'opacity-70'}
                    data-testid={`resource-submission-${s.submission_id}`}
                  >
                    <CardContent className="pt-4 space-y-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <Library className="h-4 w-4 text-amber-500 shrink-0" />
                            <p className="font-semibold truncate">{s.name}</p>
                          </div>
                          <a
                            href={s.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-[#00A896] hover:underline inline-flex items-start gap-1 break-all max-w-full"
                          >
                            <span className="break-all">{s.url}</span>
                            <ExternalLink className="h-3 w-3 shrink-0 mt-0.5" />
                          </a>
                          <p className="text-xs text-muted-foreground mt-1 break-words">
                            From {s.submitter_name} (<span className="break-all">{s.submitter_email}</span>) · {new Date(s.created_at).toLocaleString()}
                          </p>
                          {s.is_affiliated && (
                            <Badge variant="outline" className="mt-1 text-[10px]">
                              Affiliated submitter
                              {s.wants_sponsored_blog && ' · Wants $29 sponsored blog'}
                            </Badge>
                          )}
                        </div>
                        <Badge
                          variant={pending ? 'default' : s.status === 'approved' ? 'secondary' : 'outline'}
                          className="shrink-0"
                        >
                          {s.status}
                        </Badge>
                      </div>
                      {(s.categories?.length > 0) && (
                        <div className="flex flex-wrap gap-1">
                          {s.categories.map((c) => (
                            <Badge key={c} variant="secondary" className="text-[10px]">{c}</Badge>
                          ))}
                        </div>
                      )}
                      {s.about && (
                        <div className="text-sm bg-muted/30 border rounded p-2 max-h-32 overflow-y-auto">
                          <p className="text-xs font-medium uppercase text-muted-foreground mb-1">What they find useful</p>
                          {s.about}
                        </div>
                      )}
                      {pending && (
                        <div className="flex flex-wrap gap-2">
                          <Button
                            size="sm"
                            onClick={() => openPublishSubmissionDialog(s)}
                            disabled={acting === `sub-${s.submission_id}`}
                            className="bg-green-600 hover:bg-green-700 text-white"
                            data-testid={`publish-sub-${s.submission_id}`}
                          >
                            <Check className="h-4 w-4 mr-1" /> Edit & publish
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => rejectSubmission(s.submission_id)}
                            disabled={acting === `sub-${s.submission_id}`}
                            className="border-red-300 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20"
                            data-testid={`reject-sub-${s.submission_id}`}
                          >
                            <X className="h-4 w-4 mr-1" /> Reject
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })
            )}
          </TabsContent>

          {/* --- Emoji submissions tab --- */}
          <TabsContent value="emoji-submissions" className="space-y-4">
            <EmojiSubmissionsReviewPanel />
          </TabsContent>
        </Tabs>
      </CardContent>

      {/* --- Approve-with-edit dialog (blog) --- */}
      {modal?.type === 'post-approve' && (
        <Dialog open onOpenChange={(o) => !o && setModal(null)}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="approve-post-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-amber-500" />
                Approve & edit before publishing
              </DialogTitle>
              <DialogDescription className="text-xs">
                Tweak the metadata if you need to — leave anything as-is to keep the contributor's value.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label htmlFor="ap-title" className="text-xs">Title</Label>
                <Input id="ap-title" value={modal.payload.title}
                  onChange={(e) => setModal({ ...modal, payload: { ...modal.payload, title: e.target.value } })}
                  data-testid="approve-title-input" />
              </div>
              <div>
                <Label htmlFor="ap-cat" className="text-xs">Category</Label>
                <Input id="ap-cat" value={modal.payload.category}
                  onChange={(e) => setModal({ ...modal, payload: { ...modal.payload, category: e.target.value } })}
                  placeholder="e.g. Communication Tips"
                  data-testid="approve-cat-input" />
              </div>
              <div>
                <Label htmlFor="ap-tags" className="text-xs">Tags (comma-separated)</Label>
                <Input id="ap-tags" value={modal.payload.tags}
                  onChange={(e) => setModal({ ...modal, payload: { ...modal.payload, tags: e.target.value } })}
                  placeholder="ADHD, workplace, scripts"
                  data-testid="approve-tags-input" />
              </div>
              <div>
                <Label htmlFor="ap-excerpt" className="text-xs">Excerpt</Label>
                <Textarea id="ap-excerpt" rows={3} value={modal.payload.excerpt}
                  onChange={(e) => setModal({ ...modal, payload: { ...modal.payload, excerpt: e.target.value.slice(0, 500) } })}
                  data-testid="approve-excerpt-input" />
                <p className="text-[10px] text-muted-foreground mt-0.5">{modal.payload.excerpt.length}/500</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setModal(null)} data-testid="approve-cancel-btn">Cancel</Button>
              <Button onClick={submitApprove} className="bg-green-600 hover:bg-green-700 text-white" data-testid="approve-confirm-btn">
                <Check className="h-4 w-4 mr-1" /> Publish now
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* --- Send-back-with-note dialog (blog) --- */}
      {modal?.type === 'post-send-back' && (
        <Dialog open onOpenChange={(o) => !o && setModal(null)}>
          <DialogContent className="max-w-md" data-testid="send-back-dialog">
            <DialogHeader>
              <DialogTitle>Send back for edits 💚</DialogTitle>
              <DialogDescription className="text-xs">
                <strong>{modal.payload.title}</strong> — your author will get a warm email with these notes so they can polish and resubmit. We never reject blog posts — we collaborate on edits.
              </DialogDescription>
            </DialogHeader>
            <Textarea
              rows={5}
              value={modal.payload.note}
              onChange={(e) => setModal({ ...modal, payload: { ...modal.payload, note: e.target.value } })}
              placeholder="e.g. Great draft! Could you add a real-world example to the second-to-last paragraph and trim the intro a bit? Take your time — we're rooting for this piece to go live."
              data-testid="send-back-note-input"
            />
            <DialogFooter>
              <Button variant="ghost" onClick={() => setModal(null)} data-testid="send-back-cancel-btn">Cancel</Button>
              <Button onClick={submitSendBack}
                disabled={!modal.payload.note?.trim() || acting === `post-${modal.payload.post_id}`}
                className="bg-amber-500 hover:bg-amber-600 text-white"
                data-testid="send-back-confirm-btn">
                <Send className="h-4 w-4 mr-1" /> Send to author
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* --- Edit-and-publish dialog (resource submission) --- */}
      {modal?.type === 'submission-publish' && (
        <Dialog open onOpenChange={(o) => !o && setModal(null)}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="publish-sub-dialog">
            <DialogHeader>
              <DialogTitle>Edit & publish to /resources</DialogTitle>
              <DialogDescription className="text-xs">
                Refine the entry before it goes live. The original submission text from the user is pre-filled.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label htmlFor="sub-name" className="text-xs">Name</Label>
                <Input id="sub-name" value={modal.payload.name}
                  onChange={(e) => setModal({ ...modal, payload: { ...modal.payload, name: e.target.value } })}
                  data-testid="publish-sub-name-input" />
              </div>
              <div>
                <Label htmlFor="sub-url" className="text-xs">URL</Label>
                <Input id="sub-url" value={modal.payload.url}
                  onChange={(e) => setModal({ ...modal, payload: { ...modal.payload, url: e.target.value } })}
                  data-testid="publish-sub-url-input" />
              </div>
              <div>
                <Label htmlFor="sub-desc" className="text-xs">Description</Label>
                <Textarea id="sub-desc" rows={3} value={modal.payload.description}
                  onChange={(e) => setModal({ ...modal, payload: { ...modal.payload, description: e.target.value.slice(0, 2000) } })}
                  data-testid="publish-sub-desc-input" />
              </div>
              <div>
                <Label className="text-xs">Categories</Label>
                <div className="flex flex-wrap gap-1 mt-1" data-testid="publish-sub-cats">
                  {resourceCategories.map((c) => {
                    const active = modal.payload.categories.includes(c);
                    return (
                      <button
                        key={c}
                        type="button"
                        onClick={() => toggleModalCategory(c)}
                        className={`px-2 py-1 rounded-full text-xs border transition-colors ${
                          active
                            ? 'bg-[#00C9B3] text-[#0d1f2d] border-[#00C9B3]'
                            : 'bg-muted border-border hover:bg-muted/70'
                        }`}
                      >
                        {c}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setModal(null)} data-testid="publish-sub-cancel-btn">Cancel</Button>
              <Button onClick={submitPublishSubmission} className="bg-green-600 hover:bg-green-700 text-white" data-testid="publish-sub-confirm-btn">
                <Check className="h-4 w-4 mr-1" /> Publish now
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Card>
  );
};

export default AdminModerationTab;
